import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import { useCreation, useMemoizedFn } from 'ahooks';
import {
  Button,
  Card,
  message,
  Popconfirm,
  Popover,
  Space,
  Tabs,
  Typography,
  Tag,
  Tooltip,
} from 'antd';
import DecorateTitle from 'components/DecorateTitle';
import useModal from 'hooks/useModal';
import { isNil } from 'lodash-es';
import { useEffect, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import {
  createDepartment,
  deleteDepartment,
  updateDepartment,
} from 'services/api/department/department';
import { errorToasterWithServerMsg } from 'utils/errorToaster';
import { Description } from '../Group';
import { translateDepartment } from '../utils';
import { NoReceptionStoresAlert } from './components/NoReceptionStores/NoReceptionStoresAlert';
import DepartmentAccountTable from './DepartmentAccount/DepartmentAccountTable';
import DepartmentInfoFormModal, {
  DepartmentInfoFormValues,
} from './DepartmentInfo/DepartmentInfoFormModal';
import DepartmentStoreScopeStats from './DepartmentStoreScopeStats';
import { useDepartmentDetailOfId, useDepartmentList } from './hooks/data';
import RoleTable from './RoleTable';
import { DescriptionPopup } from '../components/DescriptionPopup';
import StoreConsultationDistribution from './StoreConsultationDistribution';

export interface DepartmentProps {}

const Department = (_props: DepartmentProps) => {
  const {
    visible: deptModalVisible,
    meta: deptModalMeta,
    show: showDeptModal,
    hide: hideDeptModal,
  } = useModal<{
    type: 'create' | 'edit';
    initialValues?: DepartmentInfoFormValues;
    isDefault: boolean;
  }>();
  const { t } = useTranslate();
  const modalTitle =
    deptModalMeta?.type === 'edit' ? t('department.编辑部门') : t('department.新建部门');

  const { data: depts, mutate: mutateDepts } = useDepartmentList();
  const deleteDept = useMemoizedFn(() => {
    if (isNil(activeDeptId)) {
      return;
    }
    deleteDepartment(activeDeptId)
      .then((_r) => {
        const nextDepts = depts!.filter((d) => d.id !== activeDeptId);
        mutateDepts(nextDepts);
        setActiveDept(nextDepts[0]);
        message.success(t('phrase.删除成功'));
      })
      .catch((e) => errorToasterWithServerMsg(e));
  });
  const [activeDept, setActiveDept] = useState<StoreHomeV2.Department | undefined>();
  const activeDeptId = activeDept?.id;
  const {
    data: departmentData,
    mutate: mutateDeptDetail,
    refresh: refreshDeptDetail,
  } = useDepartmentDetailOfId(activeDeptId);

  useEffect(() => {
    if (!depts?.[0] || !isNil(activeDeptId)) {
      return;
    }
    const dept = depts[0];
    setActiveDept(dept);
  }, [activeDeptId, depts]);
  const isDefault = activeDept?.isDefault === 1;
  const deptsList = useCreation(
    () =>
      depts?.map?.((dept) => {
        const title = translateDepartment(dept, t);
        return (
          // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
          <li
            key={`dept_${dept.orgId ?? 'unknown'}_${dept.id}`}
            className={activeDeptId === dept.id ? 'active' : ''}
            onClick={() => {
              setActiveDept(dept);
            }}
          >
            <Tooltip title={title}>{title}</Tooltip>
            {dept.isDefault === 1 && (
              <Tag
                className="text-primary border border-solid border-primary !bg-fill-0"
                color="blue"
              >
                {t('marketing.默认')}
              </Tag>
            )}
          </li>
        );
      }) ?? null,
    [depts, activeDeptId, t],
  );
  return (
    <Container>
      <MainCard
        title={t('nav.department')}
        extra={
          <Space size={20}>
            <Popover content={<DescriptionPopup />}>
              <Description>
                <Space>
                  {t('department.买家咨询分配说明')}
                  <QuestionCircleOutlined />
                </Space>
              </Description>
            </Popover>
            <Button
              onClick={() =>
                showDeptModal({ type: 'create', initialValues: undefined, isDefault: false })
              }
            >
              <PlusOutlined /> {t('department.新建部门')}
            </Button>
          </Space>
        }
      >
        {depts && depts.length > 0 ? (
          <Wrapper>
            <Left>
              <ul>
                <li>
                  <Button
                    onClick={() => showDeptModal({ type: 'create', isDefault: false })}
                    className={css`
                      padding: 0;
                    `}
                    type="link"
                  >
                    <PlusOutlined /> {t('department.新建部门')}
                  </Button>
                </li>
                {deptsList}
              </ul>
            </Left>
            <Right>
              <RightTitle>
                <DecorateTitle>
                  <Typography.Text ellipsis={{ tooltip: true }}>
                    {activeDept ? translateDepartment(activeDept, t) || '-' : '-'}
                  </Typography.Text>
                </DecorateTitle>
                <div>
                  <Space>
                    <Popconfirm
                      disabled={isDefault}
                      title={t('department.你确定要删除该部门吗？')}
                      onConfirm={deleteDept}
                    >
                      <Button disabled={isDefault} type="link">
                        <DeleteOutlined /> {t('department.删除部门')}
                      </Button>
                    </Popconfirm>
                    <Button
                      className="!pr-0"
                      type="link"
                      onClick={() => {
                        showDeptModal({
                          type: 'edit',
                          initialValues: departmentData
                            ? {
                                ...departmentData,
                                name:
                                  departmentData.isDefault === 1 && departmentData.name === '客服部'
                                    ? t('department.客服部')
                                    : departmentData.name,
                              }
                            : undefined,
                          isDefault: departmentData?.isDefault === 1,
                        });
                      }}
                    >
                      <EditOutlined /> {t('marketing.编辑')}
                    </Button>
                  </Space>
                </div>
              </RightTitle>
              {activeDeptId && (
                <NoReceptionStoresAlert key={activeDeptId} departmentId={activeDeptId} />
              )}
              <RightTitle>
                <div>
                  <DepartmentStoreScopeStats key={activeDeptId} departmentId={activeDeptId} />
                </div>
              </RightTitle>
              <RightBody>
                <Tabs
                  destroyInactiveTabPane
                  defaultActiveKey="accounts"
                  className={css`
                    .ant-tabs-nav {
                      padding: 0 8px;
                    }
                  `}
                >
                  <Tabs.TabPane key="accounts" tab={t('department.部门帐号')}>
                    <TabScroll>
                      {isNil(activeDeptId) ? null : (
                        <DepartmentAccountTable key={activeDeptId} departmentId={activeDeptId} />
                      )}
                    </TabScroll>
                  </Tabs.TabPane>
                  <Tabs.TabPane key="roles" tab={t('department.部门角色')}>
                    <TabScroll>
                      <RoleTable key={activeDeptId} departmentId={activeDeptId} />
                    </TabScroll>
                  </Tabs.TabPane>
                  <Tabs.TabPane key="groups" tab={t('department.部门店铺咨询分配')}>
                    <TabScroll>
                      <StoreConsultationDistribution
                        key={activeDeptId}
                        departmentId={activeDeptId!}
                      />
                    </TabScroll>
                  </Tabs.TabPane>
                </Tabs>
              </RightBody>
            </Right>
          </Wrapper>
        ) : null}
      </MainCard>
      <DepartmentInfoFormModal
        title={modalTitle}
        destroyOnClose
        isDefault={deptModalMeta?.isDefault}
        formProps={{
          initialValues: deptModalMeta?.initialValues,
          onFinish: async (values) => {
            const id = values?.id;
            const hasId = !isNil(id);
            const promise = hasId
              ? updateDepartment({ ...values, id: id! })
              : createDepartment({
                  name: values.name,
                  platformStoreScopes: values.platformStoreScopes,
                }).then((r) => {
                  if (r.id) {
                    mutateDepts([...(depts ?? []), r]);
                    setActiveDept(r);
                  }
                  return r;
                });
            await promise
              .then((r) => {
                message.success(hasId ? t('department.已更新部门') : t('department.已创建部门'));

                const nextDepts = [...(depts ?? [])];
                const idx = nextDepts.findIndex((dept) => dept.id === id);
                // @ts-expect-error
                mutateDeptDetail({ ...departmentData, ...values });

                if (hasId && idx >= 0) {
                  nextDepts[idx] = { ...nextDepts[idx], ...values };
                } else {
                  nextDepts.push(r);
                }
                mutateDepts(nextDepts);
                refreshDeptDetail();
              })
              .catch((e) => {
                if (e?.response?.data?.code === 1060) {
                  message.error(t('err_msg_code_1060'));
                  return;
                }
                if (e?.response?.data?.code === 1061) {
                  message.error(`${t('err_msg_code_1061')}${t('err_msg_code_1060')}`);
                  return;
                }
                errorToasterWithServerMsg(e, e?.response?.data?.msg);
              });
            hideDeptModal();
          },
        }}
        visible={deptModalVisible}
        onCancel={() =>
          hideDeptModal({ type: 'create', initialValues: undefined, isDefault: false })
        }
      />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 32px - var(--header-height) - var(--tabs-height));
  margin: 16px;
  border-radius: 2px;
  overflow: hidden;

  * {
    &::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 3px;
      background: hsla(0, 0%, 70.6%, 0.5);
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }
  }
`;
const MainCard = styled(Card)`
  .ant-card-head {
    margin-bottom: 0;
  }
  .ant-card-head-title {
    padding: 12px 0 11px 0;
  }
  .ant-card-body {
    padding: 0;
  }
  .ant-card-extra {
    padding: 12px 0 11px 0;
  }
`;
const Wrapper = styled.div`
  --app-ui-frame-height: calc(
    100vh - var(--header-height) - var(--tabs-height) - ${16 * 2 + 65 + 8 * 2}px
  );
  display: flex;
  flex: 1;
`;
const BasicBox = styled.div`
  padding: 10px 8px;
  flex: 1;
`;
const Left = styled(BasicBox)`
  min-width: 200px;
  flex: 0 0 auto;
  border-right: 1px solid #f0f0f0;

  [data-color-mode='dark'] & {
    border-right-color: var(--fett-border-default);

    > ul > li:not(:first-of-type) {
      &.active,
      &:hover {
        background-color: var(--fett-bg-hover);
      }
    }
  }

  > ul {
    list-style: none;
    margin: 0;
    padding: 0;
    min-height: var(--app-ui-frame-height);
    max-height: var(--app-ui-frame-height);
    overflow-y: auto;

    > li {
      display: flex;
      flex-wrap: wrap;
      max-width: 200px;
      margin: 10px 0;
      line-height: 22px;
      border-radius: 4px;
      padding: 8px 1em;

      :not(:first-of-type) {
        // 首元素为一个特殊按钮，过滤该行为
        cursor: pointer;

        &.active,
        :hover {
          background-color: #f3f6ff;
        }
      }
      > span:first-of-type {
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        word-break: break-all;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
      }
      > .ant-tag {
        margin-left: 8px;
      }
    }
  }
`;
const Right = styled(BasicBox)`
  flex: 2;
  padding: 0;
  min-height: var(--app-ui-frame-height);
  max-height: var(--app-ui-frame-height);
  overflow: auto;
`;

const RightTitle = styled.div`
  position: sticky;
  /* top: 0; */
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  border-bottom: 1px solid #f0f0f0;

  [data-color-mode='dark'] & {
    border-bottom-color: var(--fett-border-default);
  }

  ${DecorateTitle} {
    left: 0;
    margin: 0;
    line-height: 2;
  }
`;
const RightBody = styled.div`
  .ant-tabs-nav {
    padding: 0 16px;
  }
`;

const TabScroll = styled(BasicBox)`
  padding: 0 16px;
  /* overflow: auto; */
`;

export const BlueTag = styled(Tag)`
  color: #336fff;
  border: solid 1px;
  border-color: #336fff;
  background: none;
`;

export default Department;
